import { BaseModel } from '../BaseModel'
import adminApiClient from '../../api/admin-api-client'
import { Property } from '../property/Property'
/**
 * Class Company
 *
 * @extends BaseModel
 */
export class Company extends BaseModel {
  constructor(params) {
    super(params)

    this.companyId = params.companyId || 0
    this.companyName = params.companyName || ''
    this.customerNumber = params.customerNumber || null
    this.active = params.active != undefined ? params.active : true
    this.passwordPolicyId = params.passwordPolicyId || 0
    this.countryCode = params.countryCode || null
    this.companyCategoryId = params.companyCategoryId || 1 // none
    this.createdByCompanyName = params.createdByCompanyName || ''
    this.createdByCompanyId = params.createdByCompanyId || null
    this.properties = params.properties ? params.properties.map((p) => new Property(p)) : []
    this.subdivisions = params.subdivisions || []
    this.invoiceCustomerNumber = params.invoiceCustomerNumber || null
    this.allowPersonalMail = Boolean(params.allowPersonalMail) || false
    this.locale = params.locale || null
    this.fiscalId = params.fiscalId || ''
    this.entityContact = params.entityContact || null
    this.entityAddress = params.entityAddress || null
    this.phoneNumber = params.phoneNumber || null
    this.commercialPhone = params.commercialPhone || null
    this.email = params.email || null
    this.invoicingEmail = params.invoicingEmail || null
    this.trainingEmail = params.trainingEmail || null
    this.primaryAddress = params.primaryAddress || null
    this.secondaryAddress = params.secondaryAddress || null
    this.invoicingAddress = params.invoicingAddress || null
    this.productIds = params.productIds || null

    this._initialValues = { ...this, _initialValues: null }
  }

  /**
   * @override
   */
  async update() {
    this.isLoading = true
    // Fetch current entity contacts to get the IDs
    const entityContactsResponse = await adminApiClient.getCompanyEntityContacts(this.companyId)

    // Map the contact IDs from the response
    const contactIds = {}
    entityContactsResponse.data.forEach((contact) => {
      switch (contact.contactTypeId) {
        case 1:
          contactIds.phoneId = contact.contactId
          break
        case 2:
          contactIds.commercialPhoneId = contact.contactId
          break
        case 3:
          contactIds.emailId = contact.contactId
          break
        case 4:
          contactIds.invoicingEmailId = contact.contactId
          break
        case 5:
          contactIds.trainingEmailId = contact.contactId
          break
      }
    })

    // Compare initial and current entity contact values
    const changedContacts = []
    if (this.entityContact && this._initialValues.entityContact) {
      const contactFields = ['phone', 'commercialPhone', 'email', 'invoicingEmail', 'trainingEmail']
      contactFields.forEach((field, index) => {
        if (this.entityContact[field] !== this._initialValues.entityContact[field]) {
          changedContacts.push({
            contactTypeId: index + 1,
            value: this.entityContact[field],
            contactId: contactIds[`${field}Id`],
          })
        }
      })
    }

    // Update changed contacts first
    for (const contact of changedContacts) {
      if (!contact.value) {
        // Delete contact if value is empty and contactId exists
        if (contact.contactId) {
          await adminApiClient.deleteEntityContact(contact.contactId)
        }
      } else if (contact.contactId) {
        // Update existing contact
        await adminApiClient.updateEntityContact(contact.contactId, {
          entityTypeId: 1,
          contactTypeId: contact.contactTypeId,
          contact: contact.value,
          entityId: this.companyId,
        })
      } else {
        // Create new contact
        await adminApiClient.createEntityContact({
          entityTypeId: 1,
          contactTypeId: contact.contactTypeId,
          contact: contact.value,
          entityId: this.companyId,
        })
      }
    }

    // Handle entity addresses
    if (this.entityAddress && this._initialValues.entityAddress) {
      const addressTypeMap = {
        1: 'primaryAddress',
        2: 'secondaryAddress',
        3: 'invoicingAddress',
      }

      // Get current addresses to map IDs
      const entityAddressesResponse = await adminApiClient.getCompanyEntityAddresses(this.companyId)
      const existingAddresses = {}
      entityAddressesResponse.data.forEach((address) => {
        existingAddresses[addressTypeMap[address.addressTypeId]] = address
      })

      // Update or create addresses
      for (const addressTypeId of Object.keys(addressTypeMap)) {
        const newAddress = this.entityAddress[addressTypeMap[addressTypeId]]
        const existingAddress = existingAddresses[addressTypeMap[addressTypeId]]
        const initialAddress = this._initialValues.entityAddress[addressTypeMap[addressTypeId]]

        if (!newAddress && existingAddress) {
          // Delete address if it was removed
          await adminApiClient.deleteEntityAddress(existingAddress.addressId)
        } else if (newAddress && !existingAddress) {
          // Create new address
          await adminApiClient.createEntityAddress({
            entityTypeId: 1,
            entityId: this.companyId,
            addressTypeId: addressTypeId,
            ...newAddress,
          })
        } else if (newAddress && existingAddress && JSON.stringify(newAddress) !== JSON.stringify(initialAddress)) {
          // Update existing address if changed
          await adminApiClient.updateEntityAddress(existingAddress.addressId, {
            entityTypeId: 1,
            entityId: this.companyId,
            addressTypeId: addressTypeId,
            ...newAddress,
          })
        }
      }
    }

    if (this.productIds) {
      await adminApiClient.setCompanyProducts(this.companyId, this.productIds)
      this.productIds = null
    }

    const companyToBeSaved = {
      ...this,
      _initialValues: undefined,
      allowPersonalMail: this.allowPersonalMail ? 1 : 0,
      subdivisions: null,
      entityContact: null,
      entityAddress: null,
      productIds: undefined,
    }

    return adminApiClient
      .updateCompany(companyToBeSaved.companyId, { data: companyToBeSaved })
      .then((response) => {
        if (response.status === 200) {
          return true
        }
        throw new Error('Failed')
      })
      .catch((ex) => {
        this.resetInitialValues()
        throw ex
      })
      .finally(() => {
        this.isLoading = false
      })
  }

  transformForComparison(model) {
    const onlyValues = (props) => {
      return props
        .map((prop) => {
          return {
            propertyValue: prop.propertyValue ? prop.propertyValue.toString().toLowerCase() : prop.propertyValue,
          }
        })
        .filter((prop) => !!prop.propertyValue)
    }
    model.customerNumber = model.customerNumber || null
    model.properties = onlyValues(model.properties)
    return model
  }
  async refresh() {
    this.isLoading = true
    try {
      const licenseResponse = await adminApiClient.getCompany({
        companyId: this.companyId,
      })
      const newData = licenseResponse.data
      this.companyId = newData.companyId
      this.companyName = newData.companyName
      this.customerNumber = newData.customerNumber
      this.active = newData.active
      this.passwordPolicyId = newData.passwordPolicyId
      this.countryCode = newData.countryCode
      this.companyCategoryId = newData.companyCategoryId
      this.createdByCompanyName = newData.createdByCompanyName
      this.createdByCompanyId = newData.createdByCompanyId
      this.subdivisions = newData.subdivisions
      this.invoiceCustomerNumber = newData.invoiceCustomerNumber
      this.locale = newData.locale
      this.fiscalId = newData.fiscalId
      this.entityContact = newData.entityContact
      this.entityAddress = newData.entityAddress
      this.isLoading = false
      return this
    } catch (ex) {
      this.isLoading = false
      throw new Error(ex.message)
    }
  }
}
